'use client';

import { mapBrandingRow } from '@slims/shared/branding';
import { useEffect } from 'react';
import { useContextOrg } from '@/hooks/use-context-org';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { apiFetch } from '@/lib/utils/api';
import { useAuthStore } from '@/stores/auth-store';
import { useBrandingStore } from '@/stores/branding-store';

/** Branding white-label della CER di contesto (o `null` = brand SLIMS). */
export function useCerBranding() {
	return useBrandingStore((s) => s.branding);
}

/**
 * Companion di sola side-effect: carica il branding della CER di contesto e lo
 * tiene vivo via realtime. Montare esattamente una volta (app shell). Non
 * sottoscrive dati dallo store, quindi non ri-renderizza l'host.
 *
 * Si passa `useContextOrg().orgId` (la configurazione selezionata): l'RPC
 * `get_org_branding` risale `parent_id` fino alla CER, e ogni ruolo — socio
 * incluso — ha accesso alla propria configurazione (mentre solo gli admin
 * hanno una membership sulla CER top-level). Il branding resta comunque quello
 * della CER, quindi non cambia scorrendo fra configurazioni della stessa CER.
 */
export function useCerBrandingSync() {
	const contextOrg = useContextOrg();
	const loaded = useAuthStore((s) => s.loaded);
	const setBranding = useBrandingStore((s) => s.setBranding);

	const orgId = contextOrg?.orgId ?? null;
	const cerId = contextOrg?.cerId ?? null;
	const tenantId = contextOrg?.tenantId ?? null;

	useEffect(() => {
		if (!loaded || !orgId) return;
		let alive = true;
		async function fetchBranding() {
			try {
				const res = await apiFetch(`/api/cer/branding?orgId=${orgId}`);
				if (!alive) return;
				if (res.ok) {
					setBranding(mapBrandingRow(await res.json()));
				} else {
					setBranding(null);
				}
			} catch {
				if (alive) setBranding(null);
			}
		}
		fetchBranding();
		return () => {
			alive = false;
		};
	}, [orgId, loaded, setBranding]);

	// Realtime: riapplica il branding quando la riga della CER cambia (editor
	// admin/superadmin su un altro client, o su questo dopo un salvataggio).
	useEffect(() => {
		if (!orgId || !cerId || !tenantId) return;
		return subscribeToTable(
			`org-branding-${cerId}`,
			{
				event: '*',
				schema: 'public',
				table: 'organization_branding',
				filter: `organization_id=eq.${cerId}`,
			},
			async () => {
				try {
					const res = await apiFetch(`/api/cer/branding?orgId=${orgId}`);
					if (res.ok) setBranding(mapBrandingRow(await res.json()));
				} catch {
					// ignora errori transitori
				}
			},
		);
	}, [orgId, cerId, tenantId, setBranding]);
}
